/**
 * User Device Info Collector
 * Collects essential user device information in human-readable format
 */

class UserDeviceInfo {
  constructor() {
    this.sessionStart = new Date();
    this.deviceInfo = null;
  }

  /**
   * Get device type in simple format
   */
  getDeviceType() {
    const ua = navigator.userAgent;
    const width = window.innerWidth;

    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'Tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'Phone';
    }
    if (width <= 768) {
      return 'Phone';
    }
    return 'Desktop';
  }

  /**
   * Get platform (Apple/Android/Windows/Linux)
   */
  getPlatform() {
    const ua = navigator.userAgent;
    const platform = navigator.platform;

    if (/iPhone|iPad|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'Apple iOS';
    }
    if (/Mac/.test(platform)) {
      return 'Apple macOS';
    }
    if (/Android/.test(ua)) {
      return 'Android';
    }
    if (/Win/.test(platform)) {
      return 'Windows';
    }
    if (/Linux/.test(platform)) {
      return 'Linux';
    }
    return 'Unknown';
  }

  /**
   * Get browser name
   */
  getBrowser() {
    const ua = navigator.userAgent;

    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    if (ua.includes('Trident')) return 'Internet Explorer';
    if (ua.includes('Edge')) return 'Microsoft Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  }

  /**
   * Get approximate location (timezone-based)
   */
  getLocation() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffset = new Date().getTimezoneOffset();
    
    return {
      timezone,
      offset: `GMT${timezoneOffset > 0 ? '-' : '+'}${Math.abs(timezoneOffset / 60)}`,
      locale: navigator.language
    };
  }

  /**
   * Get IP address and location (requires external service)
   */
  async getIPAndLocation() {
    try {
      // Using ipapi.co for IP + location data (free tier: 1000 requests/day)
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      return {
        ip: data.ip || 'Unable to fetch',
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'Unknown',
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        postal: data.postal || 'Unknown',
        timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        org: data.org || 'Unknown' // ISP/Organization
      };
    } catch (error) {
      // Fallback if API fails
      return {
        ip: 'Unable to fetch',
        city: 'Unknown',
        region: 'Unknown',
        country: 'Unknown',
        countryCode: 'Unknown',
        latitude: null,
        longitude: null,
        postal: 'Unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        org: 'Unknown'
      };
    }
  }

  /**
   * Get screen resolution
   */
  getScreenInfo() {
    return {
      resolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio || 1
    };
  }

  /**
   * Get network information
   */
  getNetworkInfo() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      return {
        type: connection.effectiveType || 'Unknown',
        downlink: connection.downlink ? `${connection.downlink} Mbps` : 'Unknown'
      };
    }
    
    return { type: 'Unknown', downlink: 'Unknown' };
  }

  /**
   * Collect all device information
   */
  async collect() {
    const ipLocation = await this.getIPAndLocation();
    const timezoneInfo = this.getLocation();
    const screen = this.getScreenInfo();
    const network = this.getNetworkInfo();

    this.deviceInfo = {
      // Session Info
      session: {
        id: this.generateSessionId(),
        startTimestamp: this.sessionStart.getTime(),
        startDate: this.sessionStart.toISOString(),
        startDateReadable: this.sessionStart.toLocaleString(),
        endTimestamp: null,
        endDate: null,
        durationMs: null,
        durationReadable: null
      },
      
      // Device Info
      device: {
        type: this.getDeviceType(),
        platform: this.getPlatform(),
        browser: this.getBrowser(),
        userAgent: navigator.userAgent
      },
      
      // Location Info
      location: {
        ipAddress: ipLocation.ip,
        city: ipLocation.city,
        region: ipLocation.region,
        country: ipLocation.country,
        countryCode: ipLocation.countryCode,
        latitude: ipLocation.latitude,
        longitude: ipLocation.longitude,
        postal: ipLocation.postal,
        timezone: ipLocation.timezone,
        timezoneOffset: timezoneInfo.offset,
        locale: timezoneInfo.locale,
        isp: ipLocation.org
      },
      
      // Screen Info
      screen: {
        resolution: screen.resolution,
        width: window.screen.width,
        height: window.screen.height,
        viewport: screen.viewport,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        pixelRatio: screen.pixelRatio,
        colorDepth: window.screen.colorDepth,
        orientation: window.screen.orientation?.type || 'unknown'
      },
      
      // Network Info
      network: {
        type: network.type,
        speed: network.downlink,
        effectiveType: network.type
      },
      
      // Metadata
      metadata: {
        collectedAt: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer || 'direct'
      }
    };

    return this.deviceInfo;
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  logDeviceInfo() {
    if (!this.deviceInfo || process.env.NODE_ENV !== 'development') return;
  }

  logSessionEnd() {
    const sessionEnd = new Date();
    const duration = sessionEnd - this.sessionStart;
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    if (this.deviceInfo) {
      this.deviceInfo.session.endTimestamp = sessionEnd.getTime();
      this.deviceInfo.session.endDate = sessionEnd.toISOString();
      this.deviceInfo.session.durationMs = duration;
      this.deviceInfo.session.durationReadable = `${minutes}m ${seconds}s`;
    }
  }

  /**
   * Get device info object for external use
   */
  getDeviceInfo() {
    return this.deviceInfo;
  }

  /**
   * Get Firebase-ready data object
   * This returns a clean object ready to be sent to Firebase
   */
  getFirebaseData() {
    return {
      ...this.deviceInfo,
      // Add any Firebase-specific fields
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
}

// Create singleton instance
const userDeviceInfo = new UserDeviceInfo();

export default userDeviceInfo;
